import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useFetchAlertsWithPagination } from '../hooks/useFetchAlertsWithPagination';
import { StyledLayout } from './styled';
import { searchTextAtom, sortByAtom, tableFiltersAtom } from '../atoms';
import { useAtom, useAtomValue } from 'jotai';
import { getDPISidebarConfig, STATIC_SIDEBAR_FILTERS } from '../ContentPanel/utils';
import useAiAlertStore from '@src/globalStore/aiAlertStore';
import { debounce } from 'lodash';
import SidebarSection from './SidebarSection';
import ContentSection from './ContentSection';

/**
 * ContentPanelInfiniteScroll - Enhanced version of ContentPanel with infinite scroll and cursor-based pagination
 * Provides an infinite scroll view of AI alerts with server-side filtering
 */
const ContentPanelInfiniteScroll = () => {
  const [filterItems, setFilterItems] = useAtom(tableFiltersAtom);
  const { interval } = useAiAlertStore((state) => ({
    interval: state.interval,
  }));
  const searchText = useAtomValue(searchTextAtom);
  const [collapsed, setCollapsed] = useState(false);
  const sortBy = useAtomValue(sortByAtom); // Server-side sorting state
  const toggleCollapsed = () => setCollapsed(!collapsed);

  // Filter state
  const [limit] = useState(100);

  // Memoize filter params
  const memoizedFilterParams = useMemo(() => {
    const severityFilters = filterItems.filter((item) =>
      ['Critical', 'Warning', 'Info'].includes(item),
    );
    const statusFilter = filterItems.includes('Active')
      ? 'active'
      : filterItems.includes('Ended')
        ? 'ended'
        : null; // can be 'all' as well
    const metricFilters = filterItems.filter(
      (item) => !['ALL_ALERTS', 'Critical', 'Warning', 'Info', 'Active'].includes(item),
    );

    const params: any = {
      limit,
      searchText: searchText,
    };

    // Add severity filters (OR within group)
    if (severityFilters.length > 0) {
      params.severity = severityFilters;
    }

    // Add status filters (OR within group)
    if (statusFilter) {
      params.status = statusFilter;
    }

    // Add metric filters (OR within group)
    if (metricFilters.length > 0 && !filterItems.includes('ALL_ALERTS')) {
      params.metricType = metricFilters;
    }

    return params;
  }, [limit, searchText, filterItems]);

  // Fetch alerts with pagination
  const {
    alerts,
    total,
    counts: currentCounts,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
  } = useFetchAlertsWithPagination(interval, memoizedFilterParams, false, sortBy as any);

  // Sidebar loading state - simplified: show loading on first fetch
  const sidebarLoading = isFetching && alerts.length === 0;

  // Sidebar configuration
  const sidebarConfig = useMemo(() => {
    return getDPISidebarConfig({
      alerts: [],
      featureFlags: {},
      counts: currentCounts,
    });
  }, [currentCounts]);

  const { sidebarItems, defaultOpenCategories } = sidebarConfig;
  const { ALL_ALERTS } = STATIC_SIDEBAR_FILTERS;

  const onSelectMenuItem = ({ key, selectedKeys }) => {
    if (key === ALL_ALERTS) {
      setFilterItems([key]);
    } else setFilterItems(selectedKeys.filter((key) => key !== ALL_ALERTS));
  };

  const onDeselectMenuItem = ({ selectedKeys }) => {
    setFilterItems(selectedKeys);
  };

  // Infinite scroll detection
  const isLoadingMoreRef = useRef(false);

  const handleScrollDebounced = useMemo(
    () =>
      debounce((scrollTop: number, scrollHeight: number, clientHeight: number) => {
        const threshold = 100;
        const shouldLoadMore =
          scrollHeight - scrollTop <= clientHeight + threshold &&
          hasNextPage &&
          !isFetchingNextPage &&
          !isLoadingMoreRef.current;

        if (shouldLoadMore) {
          isLoadingMoreRef.current = true;
          fetchNextPage().finally(() => {
            isLoadingMoreRef.current = false;
          });
        }
      }, 300),
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  // Reset loading flag when dependencies change
  useEffect(() => {
    isLoadingMoreRef.current = false;
  }, [memoizedFilterParams]);

  // Cleanup debounced function
  useEffect(() => {
    return () => {
      handleScrollDebounced.cancel();
    };
  }, [handleScrollDebounced]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      handleScrollDebounced(scrollTop, scrollHeight, clientHeight);
    },
    [handleScrollDebounced],
  );

  // Determine if filters are applied
  const hasActiveFilters = Boolean(
    searchText || (filterItems.length > 0 && !filterItems.includes('ALL_ALERTS')),
  );

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  return (
    <StyledLayout>
      <SidebarSection
        collapsed={collapsed}
        toggleCollapsed={toggleCollapsed}
        sidebarLoading={sidebarLoading}
        sidebarItems={sidebarItems}
        filterItems={filterItems}
        defaultOpenCategories={defaultOpenCategories}
        onSelectMenuItem={onSelectMenuItem}
        onDeselectMenuItem={onDeselectMenuItem}
        currentCounts={currentCounts}
        hasActiveFilters={hasActiveFilters}
        isFetching={isFetching}
        isError={isError}
        refetch={refetch}
      />

      <ContentSection
        collapsed={collapsed}
        tableWrapperRef={tableWrapperRef}
        handleScroll={handleScroll}
        alerts={alerts}
        isFetching={isFetching}
        hasActiveFilters={hasActiveFilters}
        interval={interval}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        total={total}
        isError={isError}
        refetch={refetch}
      />
    </StyledLayout>
  );
};

export default ContentPanelInfiniteScroll;
